import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { Logo } from '@/components/ui/Logo'
import { ONTARIO_CITIES } from '@/constants/ontario-cities'
import { COUNTRY_CODES, parseToE164, splitE164 } from '@/lib/phone'
import type { SupportedCountryCode } from '@/lib/phone'

type FieldError = Partial<Record<'fullName' | 'phone' | 'licenceNumber' | 'serviceArea', string>>

export default function InstructorOnboarding() {
  const navigate = useNavigate()
  const { user, profile, instructorProfile, refreshProfile } = useAuth()

  // Pre-fill from whatever the school already captured at invite time — accept_instructor_
  // invitation() (migration 088) copies full_name/phone onto profiles and licence_number/
  // service_area-adjacent credential fields onto instructor_profiles before this page ever
  // loads, for an invited employee. A self-registered instructor (no invite) simply has
  // nothing to pre-fill from, and these all stay blank as before.
  // splitE164() only succeeds on a genuinely E.164 string (e.g. "+14165550100"). The
  // school-admin "Phone" field at invite time is a bare text input with no formatting
  // enforced, so a stored value like "4165550100" (no "+1") fails that parse — fall back
  // to showing the raw stored digits as-is rather than silently dropping them to blank;
  // the user can still correct the formatting before submitting.
  const existingPhone = profile?.phone ? splitE164(profile.phone) : null

  const [fullName, setFullName]         = useState(profile?.full_name ?? '')
  const [countryCode, setCountryCode]   = useState<SupportedCountryCode>(existingPhone?.countryCode ?? 'CA')
  const [phoneLocal, setPhoneLocal]     = useState(existingPhone?.national ?? profile?.phone ?? '')
  const [licenceNumber, setLicenceNumber] = useState(instructorProfile?.licence_number ?? '')
  const [serviceArea, setServiceArea]   = useState(instructorProfile?.service_area ?? '')

  const [fieldErrors, setFieldErrors]   = useState<FieldError>({})
  const [serverError, setServerError]   = useState<string | null>(null)
  const [submitting, setSubmitting]     = useState(false)

  function validateAndParsePhone(): string | null {
    return parseToE164(phoneLocal, countryCode)
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setServerError(null)

    const errors: FieldError = {}

    if (!fullName.trim()) {
      errors.fullName = 'Full name is required'
    }

    const e164Phone = validateAndParsePhone()
    if (!e164Phone) {
      errors.phone = 'Enter a valid phone number for the selected country'
    }

    if (!licenceNumber.trim()) {
      errors.licenceNumber = 'Instructor licence number is required'
    }

    if (!serviceArea) {
      errors.serviceArea = 'Please select your primary city'
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors)
      return
    }
    setFieldErrors({})

    setSubmitting(true)
    try {
      const { data, error } = await supabase.rpc('complete_instructor_onboarding', {
        p_full_name:      fullName.trim(),
        p_phone:          e164Phone!,
        p_licence_number: licenceNumber.trim(),
        p_service_area:   serviceArea,
      })

      if (error) {
        setServerError(error.message)
        return
      }

      // data is the jsonb return value
      const result = data as { ok: boolean; code?: string; field?: string; message?: string }

      if (!result.ok) {
        if (result.code === 'validation_error' && result.field) {
          const fieldMap: Record<string, keyof FieldError> = {
            full_name:      'fullName',
            phone:          'phone',
            licence_number: 'licenceNumber',
            service_area:   'serviceArea',
          }
          const fe = fieldMap[result.field]
          if (fe) {
            setFieldErrors({ [fe]: result.message ?? 'Invalid value' })
          } else {
            setServerError(result.message ?? 'Validation failed')
          }
        } else {
          setServerError(result.message ?? 'Could not complete onboarding. Please try again.')
        }
        return
      }

      // Refresh the auth context so ProtectedRoute sees the new onboarding_completed_at
      await refreshProfile()
      navigate('/app/instructor', { replace: true })
    } catch (err) {
      setServerError('An unexpected error occurred. Please try again.')
      console.error('[InstructorOnboarding] submit error:', err)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={{
      minHeight: '100vh', background: '#FAF6EF',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', padding: '32px 16px',
    }}>
      <div style={{ width: '100%', maxWidth: 480 }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <Logo />
        </div>

        <div style={{
          background: '#fff', borderRadius: 18,
          border: '1px solid rgba(27,42,74,0.08)',
          padding: '36px 32px',
        }}>
          <div style={{ marginBottom: 28 }}>
            <div style={{ font: '600 24px Lora, serif', color: '#1B2A4A', marginBottom: 6 }}>
              Complete your profile
            </div>
            <div style={{ font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.55)' }}>
              This information is required before you can enrol students.
            </div>
          </div>

          {/* Read-only email */}
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Email</label>
            <div style={{
              padding: '10px 14px', background: 'rgba(27,42,74,0.04)',
              border: '1px solid rgba(27,42,74,0.10)', borderRadius: 9,
              font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.5)',
            }}>
              {user?.email}
            </div>
          </div>

          <form onSubmit={handleSubmit} noValidate>
            {/* Full name */}
            <div style={{ marginBottom: 18 }}>
              <label style={labelStyle} htmlFor='onboarding-name'>Full name</label>
              <input
                id='onboarding-name'
                type='text'
                value={fullName}
                onChange={e => setFullName(e.target.value)}
                autoComplete='name'
                placeholder='Jane Smith'
                style={inputStyle(!!fieldErrors.fullName)}
              />
              {fieldErrors.fullName && <div style={errorStyle}>{fieldErrors.fullName}</div>}
            </div>

            {/* Phone: country selector + number */}
            <div style={{ marginBottom: 18 }}>
              <label style={labelStyle} htmlFor='onboarding-phone'>Phone number</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <select
                  value={countryCode}
                  onChange={e => setCountryCode(e.target.value as SupportedCountryCode)}
                  style={{
                    ...inputStyle(false),
                    flex: '0 0 auto', width: 'auto', paddingRight: 32,
                    cursor: 'pointer',
                  }}
                  aria-label='Country code'
                >
                  {COUNTRY_CODES.map(c => (
                    <option key={c.code} value={c.code}>{c.label}</option>
                  ))}
                </select>
                <input
                  id='onboarding-phone'
                  type='tel'
                  value={phoneLocal}
                  onChange={e => setPhoneLocal(e.target.value)}
                  autoComplete='tel-national'
                  placeholder='416 555 0100'
                  style={{ ...inputStyle(!!fieldErrors.phone), flex: 1 }}
                />
              </div>
              {fieldErrors.phone && <div style={errorStyle}>{fieldErrors.phone}</div>}
            </div>

            {/* Instructor licence number */}
            <div style={{ marginBottom: 18 }}>
              <label style={labelStyle} htmlFor='onboarding-licence'>Instructor licence number</label>
              <input
                id='onboarding-licence'
                type='text'
                value={licenceNumber}
                onChange={e => setLicenceNumber(e.target.value)}
                autoComplete='off'
                placeholder='MTO-XXXXXXXX'
                style={inputStyle(!!fieldErrors.licenceNumber)}
              />
              {fieldErrors.licenceNumber && <div style={errorStyle}>{fieldErrors.licenceNumber}</div>}
            </div>

            {/* Primary city */}
            <div style={{ marginBottom: 28 }}>
              <label style={labelStyle} htmlFor='onboarding-city'>Primary city</label>
              <select
                id='onboarding-city'
                value={serviceArea}
                onChange={e => setServiceArea(e.target.value)}
                style={{ ...inputStyle(!!fieldErrors.serviceArea), cursor: 'pointer' }}
              >
                <option value=''>Select a city…</option>
                {ONTARIO_CITIES.map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
              {fieldErrors.serviceArea && <div style={errorStyle}>{fieldErrors.serviceArea}</div>}
            </div>

            {serverError && (
              <div className='banner-error' style={{ marginBottom: 20 }}>
                {serverError}
              </div>
            )}

            <button
              type='submit'
              disabled={submitting}
              className='btn-teal'
              style={{ width: '100%', opacity: submitting ? 0.65 : 1 }}
            >
              {submitting ? 'Saving…' : 'Complete profile'}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  font: '500 13px Inter, sans-serif',
  color: '#1B2A4A',
  marginBottom: 6,
}

function inputStyle(hasError: boolean): React.CSSProperties {
  return {
    width: '100%',
    padding: '10px 14px',
    border: `1px solid ${hasError ? '#C0522A' : 'rgba(27,42,74,0.18)'}`,
    borderRadius: 9,
    font: '400 14px Inter, sans-serif',
    color: '#1B2A4A',
    background: '#fff',
    outline: 'none',
    boxSizing: 'border-box',
  }
}

const errorStyle: React.CSSProperties = {
  marginTop: 5,
  font: '400 12.5px Inter, sans-serif',
  color: '#C0522A',
}
